import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CarIcon, FactoryIcon, LaptopIcon, BoxIcon,
  TowerIcon, ChartIcon, HandshakeIcon, PillarIcon
} from "../Icons";

const clients = [
  {
    icon: <CarIcon size={40} />,
    title: "EV Manufacturers",
    tag: "OEM Partners",
    body: "Two-wheelers, three-wheelers, and four-wheelers producing lithium-ion powered vehicles are subject to EPR obligations under the Battery Waste Management Rules 2022. We serve as their registered recycling partner — handling collection logistics, safe processing, and EPR certificate issuance.",
    needs: ["EPR compliance certificates", "Volume-based SLA", "National collection coverage"],
  },
  {
    icon: <FactoryIcon size={40} />,
    title: "Battery Producers",
    tag: "Cell & Pack Manufacturers",
    body: "Cell manufacturers and battery pack assemblers generate production scrap and warranty returns that require certified disposal. We provide compliant downstream processing and can supply Black Mass back to the battery supply chain — closing the loop for forward-thinking manufacturers.",
    needs: ["Production scrap disposal", "Warranty return processing", "Black Mass buyback option"],
  },
  {
    icon: <LaptopIcon size={40} />,
    title: "Electronics Companies",
    tag: "Consumer Tech Brands",
    body: "Brands selling laptops, mobile phones, tablets, power banks, and IoT devices with lithium batteries face EPR obligations across the electronics and battery waste streams simultaneously. We handle the battery component with full compliance documentation.",
    needs: ["Multi-format battery acceptance", "EPR certificates", "Retailer collection integration"],
  },
  {
    icon: <BoxIcon size={40} />,
    title: "Scrap Aggregators",
    tag: "Collection Intermediaries",
    body: "Traders and collection agencies that gather battery waste from across the informal and formal sectors need a certified, compliant downstream processor to avoid liability. We are that downstream partner — providing intake documentation, material receipt, and processing certificates.",
    needs: ["Certified downstream processing", "Intake documentation", "Volume flexibility"],
  },
  {
    icon: <TowerIcon size={40} />,
    title: "Energy Storage Operators",
    tag: "BESS & Grid Storage",
    body: "Battery Energy Storage System (BESS) operators and grid-scale storage projects face decommissioning at end-of-project-life. We handle the full decommissioning and recycling process for large-format stationary batteries — with the safety protocols and facility size to manage significant volumes.",
    needs: ["Large-format battery handling", "Decommissioning support", "Site-based pickup"],
  },
  {
    icon: <ChartIcon size={40} />,
    title: "Investors & VCs",
    tag: "Capital Partners",
    body: "India's critical minerals and battery recycling market is one of the most significant clean-tech investment opportunities of this decade. We are open to conversations with investors who want direct exposure to the battery circular economy — at an early stage, with real operational traction.",
    needs: ["Equity investment", "Strategic partnership", "Technology co-investment"],
  },
  {
    icon: <HandshakeIcon size={40} />,
    title: "Technology Partners",
    tag: "R&D Collaborators",
    body: "We partner with research institutions, IITs, and technology companies working on advanced hydrometallurgical or direct recycling processes. Our facility serves as a real-world testbed for processing innovations — connecting lab-scale breakthroughs to industrial-scale volumes.",
    needs: ["Black Mass supply for R&D", "Process pilot testing", "JV for tech scale-up"],
  },
  {
    icon: <PillarIcon size={40} />,
    title: "Government & PSUs",
    tag: "Public Sector",
    body: "We work with public sector undertakings, state governments, and policy bodies to support India's critical mineral self-sufficiency agenda. From supply chain data to EPR infrastructure gaps, we engage constructively with the institutions shaping this sector's future.",
    needs: ["Policy data and inputs", "EPR infrastructure", "Public-private collaboration"],
  },
];

function WhoWeWorkWithPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const reveals = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("visible"); observer.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    reveals.forEach((r) => observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <main style={{ paddingTop: "80px" }}>

      {/* PAGE HERO */}
      <section className="page-hero">
        <div className="hero-grid-bg"></div>
        <div className="hero-glow" style={{ opacity: 0.4 }}></div>
        <div className="section-inner" style={{ position: "relative", zIndex: 2 }}>
          <div className="section-tag">Partners &amp; Clients</div>
          <h1 className="section-title display" style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", marginBottom: "1.2rem" }}>
            Who We Work With
          </h1>
          <p className="section-body" style={{ maxWidth: "620px" }}>
            Battery recycling touches every layer of India's EV and energy ecosystem. We've built our service model to work with each of them.
          </p>
        </div>
      </section>

      {/* CLIENT DEEP DIVES */}
      <section>
        <div className="section-inner">
          <div className="section-tag">Stakeholder Profiles</div>
          <h2 className="section-title display">Built for Every Partner</h2>

          <div className="partner-grid reveal">
            {clients.map((c) => (
              <div className="partner-card" key={c.title}>
                <div className="partner-card-header">
                  <div className="partner-icon">{c.icon}</div>
                  <div>
                    <div className="section-tag" style={{ marginBottom: ".2rem" }}>{c.tag}</div>
                    <h3>{c.title}</h3>
                  </div>
                </div>
                <p>{c.body}</p>
                <div className="partner-needs">
                  <div className="partner-needs-label">What We Provide</div>
                  <ul>
                    {c.needs.map((n) => <li key={n}>{n}</li>)}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON THREAD */}
      <section style={{ background: "var(--panel)" }}>
        <div className="section-inner">
          <div className="section-tag">Common Ground</div>
          <h2 className="section-title display">What Every Partner Gets</h2>
          <p className="section-body">
            Regardless of sector or scale, every Alankaar Power partner receives the same non-negotiables.
          </p>
          <div className="common-grid reveal">
            {[
              "EPR compliance certificates for all batteries processed",
              "Full chain-of-custody documentation from intake to output",
              "Hazardous waste handling in full regulatory compliance",
              "Material test certificates for recovered Black Mass",
              "Dedicated account contact for volume and logistics queries",
              "Transparent reporting on collection and recovery volumes",
            ].map((item, i) => (
              <div className="common-item" key={i}>
                <div className="common-check">✓</div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ borderTop: "var(--accent-line)" }}>
        <div className="section-inner" style={{ textAlign: "center" }}>
          <h2 className="section-title display">Find Your Partnership Model</h2>
          <p className="section-body" style={{ margin: "0 auto 2rem" }}>
            Tell us who you are and what you need. We'll structure the right arrangement.
          </p>
          <div style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/#contact" className="btn-primary">Start the Conversation</a>
            <Link to="/" className="btn-outline">← Back to Home</Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default WhoWeWorkWithPage;